/* NOTE: This service now only manages CLIPPED PRINT COUPONS. Historically, it handled all
	* item clippings as well, but those are now maintained by the shopping list sync API.
	* We should consider refactoring or renaming this service to indicate that it only manages
	* print coupons.
	*
	* This Service is also responsible for migrating old clippings into the SL
	*/

// Imports ------------------------------------------------------------------->

import {ClippingType, Clipping, ClippingItemDetails} from "../definitions/CommonDefinitions";
import {Coupon} from "../definitions/CouponDefinitions";
import {Service, service, inject} from "../services/di";
import {StorageService} from "../services/StorageService";
import {EventService} from "../services/EventService";
import {ShoppingListService, SLFlyerItemClipping} from "../services/ShoppingListService";

// Private Definitions ------------------------------------------------------->

interface ClippingStorage {
	[id: string]: Clipping;
}

// Public Definitions -------------------------------------------------------->

export class ClippingPrintItemAdapter implements ClippingItemDetails {

	private coupon: Coupon;
	constructor(coupon: Coupon) {
		this.coupon = coupon;
	}

	get id() {
		return `${this.coupon.id}`;
	}

	get validTo() {
		return new Date(this.coupon.validTo.getTime()).toString();
	}

	get imageUrl() {
		return this.coupon.imageUrl;
	}
}

@service
export class ClippingService extends Service {

	@inject storage: StorageService;
	@inject eventService: EventService;
	@inject shoppingList: ShoppingListService;

	private _clippings: ClippingStorage = this._loadClippings();

	get count() {
		if (!this._clippings) {
			return 0;
		}
		return Object.getOwnPropertyNames(this._clippings).length;
	}

	addClipping(type: ClippingType, item: ClippingItemDetails, merchant: string, merchantLogo?: string) {
		this._clippings[item.id] = {
			type,
			merchant,
			item,
			merchantLogo
		};
		this._saveClippings();
	}

	removeClipping(id: string) {
		delete this._clippings[id];
		this._saveClippings();
	}

	hasClipping(id: string) {
		return this._clippings[id];
	}

	getClipping(id: string) {
		return this._clippings[id];
	}

	toggleClipping(type: ClippingType, item: ClippingItemDetails, merchant: string, merchantLogo?: string) {
		if (this._clippings[item.id]) {
			delete this._clippings[item.id];
			this._saveClippings();
			return false;
		} else {
			this._clippings[item.id] = {
				type,
				item,
				merchant,
				merchantLogo
			};
			this._saveClippings();
			return true;
		}
	}

	deleteExpiredClippings() {
		const left: { [id: string]: Clipping } = {};
		const now = new Date;
		for (const k in this._clippings) {
			if (this._clippings.hasOwnProperty(k)) {
				const clipping = this._clippings[k];
				if (now < new Date(clipping.item.validTo))
					left[k] = clipping;
			}
		}

		this._clippings = left;
		this._saveClippings();
	}

	deleteAllClippings() {
		this._clippings = {};
		this._saveClippings();
	}

	getClippings(): Clipping[] {
		const clippings: Clipping[] = [];
		for (const k in this._clippings)
			if (this._clippings.hasOwnProperty(k))
				clippings.push(this._clippings[k]);
		return clippings;
	}


	private _saveClippings(data?: ClippingStorage) {
		this.storage.setItem("clippings", JSON.stringify({
			version: 3,
			clippings: data || this._clippings
		}));

		this.eventService.fireClippingsChangedEvent();
	}

	private _loadClippings() {
		const data = JSON.parse(this.storage.getItem("clippings") || "{}");
		if (!data.clippings) {
			return {};
		}

		let migrated = false;
		Object.getOwnPropertyNames(data.clippings).forEach((id: string) => {
			const clipping = data.clippings[id];

			// V1 and V2 of this data could contain clipped items. As of V3, we're only maintaining
			// print coupons so ignore all other item types
			if (data.version === 1) {
				migrated = true;
				const details = JSON.parse(JSON.stringify(clipping.item));
				details.flyerItemId = details.id;
				details.merchantName = clipping.merchant;
				details.merchantLogoUrl = null; // No merchant logo available for v1 clippings
				details.thumbnailUrl = details.cutout_image_url;
				this.shoppingList.localList.add([new SLFlyerItemClipping(details)]);
				delete data.clippings[id];
				return;
			} else if (data.version === 2 && clipping.type === ClippingType.ITEM) {
				migrated = true;
				const details = JSON.parse(JSON.stringify(clipping.item.item));
				details.flyerItemId = details.id;
				details.merchantName = clipping.merchant;
				details.merchantLogoUrl = clipping.merchantLogo;
				details.thumbnailUrl = details.cutout_image_url;
				this.shoppingList.localList.add([new SLFlyerItemClipping(details)]);
				delete data.clippings[id];
				return;
			}

			// Lift 'item' property using the appropriate adapter
			if (clipping.type === ClippingType.PRINT_COUPON) {
				const validTo = clipping.item.coupon.validTo as string;
				const coupon = clipping.item.coupon as Coupon;
				coupon.validTo = new Date(validTo);
				clipping.item = new ClippingPrintItemAdapter(coupon);
			}
		});

		if (migrated) {
			this._saveClippings(data.clippings);
		}

		return data.clippings || {};
	}
}
